import { Text } from "react-native-paper";
import type { IconType } from "../../../icon";
import { useAppTheme } from "../../../useAppTheme";
import { AppIcon } from "../../AppIcon";
import Box from "../../Box";
import DonutChart from "../../DonutChart";
import { SessionReportDonutSurfaceHalo } from "./SessionReportDonutSurfaceHalo";

const SUMMARY_DONUT_RADIUS = 50;
const SUMMARY_DONUT_INNER_RADIUS = 42;
const COMPACT_DONUT_RADIUS = 39;
const COMPACT_DONUT_INNER_RADIUS = 32;
const LARGE_DONUT_RADIUS = 70;
const LARGE_DONUT_INNER_RADIUS = 59;

const DONUT_DIMENSIONS = {
	compact: {
		radius: COMPACT_DONUT_RADIUS,
		innerRadius: COMPACT_DONUT_INNER_RADIUS,
		contentSize: 65,
		scoreVariant: "labelLarge",
	},
	summary: {
		radius: SUMMARY_DONUT_RADIUS,
		innerRadius: SUMMARY_DONUT_INNER_RADIUS,
		contentSize: 83,
		scoreVariant: "headlineSmall",
	},
	large: {
		radius: LARGE_DONUT_RADIUS,
		innerRadius: LARGE_DONUT_INNER_RADIUS,
		contentSize: 116,
		scoreVariant: "headlineMedium",
	},
} as const;

type Props = {
	label?: string;
	score: number;
	maxScore: number;
	progress: number;
	icon?: IconType;
	size?: "summary" | "compact" | "large";
	/** Surface halo behind the ring when it overlaps the card edge (single-score). */
	showSurfaceHalo?: boolean;
};

export const SessionReportScoreDonut = ({
	label,
	score,
	maxScore,
	progress,
	icon,
	size = "summary",
	showSurfaceHalo = false,
}: Props) => {
	const theme = useAppTheme();
	const progressPercent = Math.max(0, Math.min(1, progress)) * 100;
	const colorScale = [theme.colors.primary, theme.palette.progress.none];
	const isCompact = size === "compact";
	const { radius, innerRadius, contentSize, scoreVariant } =
		DONUT_DIMENSIONS[size];
	const donutSize = radius * 2;

	return (
		<Box gap={8} alignItems="center">
			<Box
				position="relative"
				width={donutSize}
				height={donutSize}
				alignItems="center"
				justifyContent="center"
			>
				{showSurfaceHalo ? (
					<SessionReportDonutSurfaceHalo size={donutSize} />
				) : null}
				<DonutChart
					radius={radius}
					innerRadius={innerRadius}
					distribution={[progressPercent, 100 - progressPercent]}
					variant="progress"
					colorScale={colorScale}
					score={progressPercent}
				>
					<Box
						width={contentSize}
						height={contentSize}
						borderRadius={contentSize / 2}
						alignItems="center"
						justifyContent="center"
						backgroundColor={theme.colors.elevation.level1}
					>
						<Box flexDirection="row" alignItems="flex-end" gap={1}>
							<Text
								variant={scoreVariant}
								style={{ color: theme.colors.onSurface }}
							>
								{Math.round(score)}
							</Text>
							{isCompact ? null : (
								<Box paddingBottom={2}>
									<Text
										variant="labelMedium"
										style={{ color: theme.colors.onSurface }}
									>
										/{Math.round(maxScore)}
									</Text>
								</Box>
							)}
						</Box>
					</Box>
				</DonutChart>
			</Box>
			{label ? (
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="center"
					gap={4}
				>
					{icon ? (
						<AppIcon name={icon} size={24} color={theme.colors.onSurface} />
					) : null}
					<Text
						variant="titleSmall"
						style={{ color: theme.colors.onSurface, textAlign: "center" }}
					>
						{label}
					</Text>
				</Box>
			) : null}
		</Box>
	);
};
